/**
 * Session applicative (cookies JWT httpOnly + indicateur sessionStorage).
 * Ne jamais stocker access/refresh JWT dans localStorage.
 */
import {
  getSessionItem,
  setSessionItem,
  removeSessionItem,
  removeLocalItem,
  getLocalItem,
} from '@/utils/storage';
import { setVerifiedProfile, clearVerifiedProfile } from '@/utils/verifiedProfile';

export const AUTH_SESSION_KEY = 'auth_session';
export const CSRF_COOKIE_NAME = 'pat_csrftoken';

const LEGACY_JWT_KEYS = ['access_token', 'refresh_token', 'jwt_token'];

/** Profil UI (sessionStorage uniquement — pas de JWT, pas de persistance longue). */
export const PROFILE_SESSION_KEYS = [
  'user_role',
  'user_id',
  'user_permissions',
  'acces_donnees_autorise',
  'type_client',
  'analysis_access_granted',
];

const LEGACY_PROFILE_LOCAL_KEYS = [
  ...PROFILE_SESSION_KEYS,
  'nom',
  'prenom',
  'email',
  'username',
];

const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

const VERIFY_TTL_MS = 30_000;
let verifyPromise = null;
let lastVerifiedAt = 0;

export function getCsrfTokenFromCookie() {
  try {
    const escaped = CSRF_COOKIE_NAME.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${escaped}=([^;]*)`));
    return m ? decodeURIComponent(m[1]).trim() : '';
  } catch {
    return '';
  }
}

export function setAuthenticatedSession() {
  setSessionItem(AUTH_SESSION_KEY, '1');
}

export function clearAuthenticatedSession() {
  removeSessionItem(AUTH_SESSION_KEY);
  clearUserProfileSession();
  purgeLegacyJwtStorage();
}

export function isAuthenticatedSession() {
  return getSessionItem(AUTH_SESSION_KEY) === '1';
}

export function purgeLegacyJwtStorage() {
  LEGACY_JWT_KEYS.forEach((k) => {
    removeLocalItem(k);
    removeSessionItem(k);
  });
}

export function clearUserProfileSession() {
  PROFILE_SESSION_KEYS.forEach((k) => removeSessionItem(k));
  LEGACY_PROFILE_LOCAL_KEYS.forEach((k) => removeLocalItem(k));
  clearVerifiedProfile();
}

/** Déplace l'ancien profil localStorage → sessionStorage (une fois). */
export function migrateLegacyProfileToSession() {
  LEGACY_PROFILE_LOCAL_KEYS.forEach((key) => {
    const v = getLocalItem(key);
    if (v != null && v !== '') {
      setSessionItem(key, v);
      removeLocalItem(key);
    }
  });
}

export function getProfileSessionItem(key) {
  return getSessionItem(key);
}

export function getUserRoleFromSession() {
  const role = getSessionItem('user_role');
  return role ? String(role).trim().toLowerCase() : '';
}

/** Identifiant utilisateur (session), pour formulaires client. */
export function getProfileUserId() {
  const raw =
    getSessionItem('user_id') || getSessionItem('id') || getSessionItem('pk');
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Stocke le profil renvoyé par l'API (login ou GET /api/profile/).
 */
export function storeUserProfileFromApi(userData) {
  if (!userData || typeof userData !== 'object') return;
  purgeLegacyJwtStorage();
  setAuthenticatedSession();
  setVerifiedProfile(userData);

  const rawRole = userData.role || userData.user_role || userData.userRole;
  if (rawRole) {
    setSessionItem('user_role', String(rawRole).trim().toLowerCase());
  }
  const role = (rawRole && String(rawRole).trim().toLowerCase()) || '';
  if (['saisie', 'validation', 'admin', 'employe'].includes(role)) {
    const acces = userData.acces_donnees_autorise === true;
    setSessionItem('acces_donnees_autorise', acces ? 'true' : 'false');
  } else {
    setSessionItem('acces_donnees_autorise', 'true');
  }
  if (userData.type_client) {
    setSessionItem('type_client', String(userData.type_client));
  }
  if (Array.isArray(userData.permissions)) {
    setSessionItem('user_permissions', JSON.stringify(userData.permissions));
  } else {
    removeSessionItem('user_permissions');
  }
  if (userData.username) {
    setSessionItem('username', String(userData.username));
  }
  const uid = userData.id ?? userData.user_id ?? userData.pk;
  if (uid != null && uid !== '') {
    setSessionItem('user_id', String(uid));
  }
  markSessionVerifiedFromLogin();
}

/** @deprecated alias login */
export function storeUserProfileFromLogin(userData) {
  return storeUserProfileFromApi(userData);
}

/**
 * Vérifie la session auprès du serveur (cookies httpOnly).
 * Empêche l'usurpation via auth_session=1 forgé sans cookie valide.
 */
export async function verifyServerSession(axiosInstance) {
  if (!isAuthenticatedSession()) {
    return false;
  }
  const now = Date.now();
  if (lastVerifiedAt && now - lastVerifiedAt < VERIFY_TTL_MS) {
    return true;
  }
  if (verifyPromise) {
    return verifyPromise;
  }

  verifyPromise = axiosInstance
    .get('/api/profile/', {
      withCredentials: true,
      timeout: 20000,
      __sessionVerify: true,
    })
    .then((res) => {
      storeUserProfileFromApi(res.data);
      lastVerifiedAt = Date.now();
      return true;
    })
    .catch((err) => {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        clearAuthenticatedSession();
        lastVerifiedAt = 0;
      }
      return false;
    })
    .finally(() => {
      verifyPromise = null;
    });

  return verifyPromise;
}

export async function ensureAuthenticated(axiosInstance) {
  if (!isAuthenticatedSession()) {
    return false;
  }
  return verifyServerSession(axiosInstance);
}

export function invalidateServerSessionCache() {
  lastVerifiedAt = 0;
  verifyPromise = null;
}

/** Après login/MFA réussi : évite un GET /api/profile/ immédiat avant que les cookies soient visibles. */
export function markSessionVerifiedFromLogin() {
  lastVerifiedAt = Date.now();
  verifyPromise = null;
}

export function applyCsrfHeader(config) {
  const cfg = config || {};
  cfg.headers = cfg.headers || {};
  const method = String(cfg.method || 'get').toLowerCase();
  if (!UNSAFE_METHODS.has(method)) {
    return cfg;
  }
  const csrf = getCsrfTokenFromCookie();
  if (csrf) {
    cfg.headers['X-CSRF-Token'] = csrf;
  }
  return cfg;
}

/** Options axios communes (cookies cross-origin API). */
export function credentialedConfig(extra = {}) {
  return {
    withCredentials: true,
    ...extra,
    headers: {
      ...(extra.headers || {}),
    },
  };
}

export function isAuthlessApiUrl(url) {
  const u = String(url || '');
  return (
    /\/api\/login\//.test(u) ||
    /\/api\/login\/verify-mfa\//.test(u) ||
    /\/api\/logout\//.test(u) ||
    /\/api\/token\/refresh\//.test(u) ||
    /\/api\/password-reset\//.test(u) ||
    /\/api\/utilisateurs\/create\//.test(u) ||
    /\/api\/contact-public\//.test(u) ||
    /\/api\/payments\/diagnostic-checkout\//.test(u) ||
    /\/api\/email-verification\//.test(u) ||
    /\/api\/db-health\//.test(u) ||
    /\/healthz/.test(u)
  );
}

function isLogoutUrl(url) {
  return /\/api\/logout\//.test(String(url || ''));
}

export function installAxiosAuth(axiosInstance, { router } = {}) {
  axiosInstance.defaults.withCredentials = true;

  axiosInstance.interceptors.request.use((config) => {
    const cfg = { ...config };
    cfg.headers = { ...(cfg.headers || {}) };
    if (isAuthlessApiUrl(cfg.url)) {
      delete cfg.headers.Authorization;
      return cfg;
    }
    delete cfg.headers.Authorization;
    return applyCsrfHeader(cfg);
  });

  let refreshPromise = null;
  let isRedirectingOn401 = false;
  let sessionCleanupInProgress = false;

  function isRefreshUrl(url) {
    return /\/api\/token\/refresh\//.test(String(url || ''));
  }

  function redirectToLoginIfNeeded() {
    const current = router?.currentRoute?.value?.fullPath;
    if (!router || isRedirectingOn401 || current === '/connexion') {
      return;
    }
    isRedirectingOn401 = true;
    const query = {};
    if (current && current !== '/' && !current.startsWith('/connexion')) {
      query.redirect = current;
    }
    router.replace({ path: '/connexion', query }).finally(() => {
      isRedirectingOn401 = false;
    });
  }

  async function refreshSession() {
    if (!refreshPromise) {
      refreshPromise = axiosInstance
        .post('/api/token/refresh/', {}, {
          timeout: 20000,
          withCredentials: true,
          __isTokenRefresh: true,
        })
        .then((res) => res)
        .catch(() => null)
        .finally(() => {
          refreshPromise = null;
        });
    }
    return refreshPromise;
  }

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      try {
        const config = error?.config;
        const status = error?.response?.status;
        if (status !== 401 || !config) {
          return Promise.reject(error);
        }
        // Login / logout / refresh : ne pas enchaîner refresh + logout (évite des centaines de POST /logout/).
        if (isAuthlessApiUrl(config.url) || config.__skipSessionRecovery) {
          return Promise.reject(error);
        }
        if (config.__sessionVerify) {
          clearAuthenticatedSession();
          invalidateServerSessionCache();
          redirectToLoginIfNeeded();
          return Promise.reject(error);
        }
        if (config.__isTokenRefresh || isRefreshUrl(config.url) || isLogoutUrl(config.url)) {
          clearAuthenticatedSession();
          invalidateServerSessionCache();
          redirectToLoginIfNeeded();
          return Promise.reject(error);
        }
        if (!config.__retryAfterRefresh) {
          config.__retryAfterRefresh = true;
          const refreshed = await refreshSession();
          if (refreshed) {
            invalidateServerSessionCache();
            return axiosInstance.request(config);
          }
        }

        clearAuthenticatedSession();
        invalidateServerSessionCache();

        if (!sessionCleanupInProgress) {
          sessionCleanupInProgress = true;
          try {
            await axiosInstance.post(
              '/api/logout/',
              {},
              {
                withCredentials: true,
                timeout: 10000,
                __skipSessionRecovery: true,
              }
            );
          } catch (_) {
            /* déjà déconnecté côté serveur */
          } finally {
            sessionCleanupInProgress = false;
          }
        }

        redirectToLoginIfNeeded();
      } catch (_) {
        /* ignore */
      }
      return Promise.reject(error);
    }
  );
}
